import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StudentDetails, TripDates } from '@lx/quote-tool/shared/types';
import { getQuoteToolStorageKey } from '@lx/quote-tool/lite/shared/types';
import { SessionStorageService } from '@lx/shared/storage';
import { getCompany } from '@lx/shared/tokens';
import { COMPANIES } from '@lx/shared/types';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, isObservable, of, skip, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { StepDefinitionService } from '../services';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { AyaDuration, BaseAccommodation, HOMESTAY, OTHER, RouteIds, Step, STORE_RESET_KEY } from '../types';
import { Timezone } from '@lx/quote-tool/shared/types';
import { ClassTime } from '@lx/quote-tool/lite/online/types';
import { ResidenceDetails } from '../types/residence-details.type';
import { QuoteToolLiteState } from './quote-tool-lite.state';
import { SharedDataStore } from './shared-data';
import { Flows, Flow, SchoolLocation } from '@lx/quote-tool/lite/shared/types';

/**
 * Auxiliary type for the selected course details (used on courseDetails$ selector)
 */
type SelectedCourseDetails = {
  selectedLanguage: string;
  selectedSchoolLocation: SchoolLocation;
  selectedCourse?: CourseDetails;
  selectedDuration: number;
  selectedAyaDuration?: AyaDuration;
  selectedStartDate: string;
  selectedEndDate: string;
  selectedTimezone?: Timezone;
  selectedClassTime?: ClassTime;
};

@Injectable({ providedIn: 'root' })
export class QuoteToolLiteStore extends ComponentStore<QuoteToolLiteState> {
  // Selectors
  readonly currentStepId$ = this.select(({ currentStep }) => currentStep?.id);
  readonly currentStep$ = this.select(({ currentStep }) => currentStep);
  readonly activeFlow$ = this.select(({ activeFlow }) => activeFlow);
  readonly selectedLanguage$ = this.select(({ selectedLanguage }) => selectedLanguage);
  readonly selectedSchoolLocation$ = this.select(({ selectedSchoolLocation }) => selectedSchoolLocation);
  readonly selectedCourse$ = this.select(({ selectedCourse }) => selectedCourse);
  readonly productPackage$ = this.select(({ selectedCourse }) => selectedCourse?.productPackage);
  readonly selectedDuration$ = this.select(({ selectedDuration }) => selectedDuration);
  readonly selectedAccommodation$ = this.select(({ selectedAccommodation }) => selectedAccommodation);
  readonly selectedCurrency$ = this.select(({ selectedCurrency }) => selectedCurrency);
  readonly selectedTransferRows$ = this.select(({ selectedTransferRows }) => selectedTransferRows);
  readonly studentDetails$ = this.select(({ studentDetails }) => studentDetails);
  readonly deeplinkType$ = this.select(({ deeplinkType }) => deeplinkType);
  readonly selectedProfessionalImmersionExperienceCode$ = this.select(
    ({ selectedProfessionalImmersionExperienceCode }) => selectedProfessionalImmersionExperienceCode,
  );
  readonly selectedPremiumActivities$ = this.select(({ selectedPremiumActivities }) => selectedPremiumActivities);
  readonly upselling$ = this.select(({ upselling }) => upselling);
  readonly studentFirstName$ = this.select(({ studentDetails }) => studentDetails?.firstName);
  readonly selectedPremiumAccommodation$ = this.select(({ selectedPremiumAccommodationCode }) => selectedPremiumAccommodationCode);
  readonly selectedStartDate$ = this.select(({ selectedStartDate }) => selectedStartDate);
  readonly selectedEndDate$ = this.select(({ selectedEndDate }) => selectedEndDate);
  readonly requestedQuotes$ = this.select(({ requestedQuotes }) => requestedQuotes);
  readonly currentStepNextRouteId$ = this.select(({ currentStep }) => {
    if (!currentStep?.nextStep) {
      return of(RouteIds.course);
    }

    const nextStep = currentStep.nextStep();
    return (isObservable(nextStep) ? nextStep : of(nextStep)).pipe(map((routeId: RouteIds) => routeId));
  });

  readonly selectedDates$ = this.select(
    ({ selectedStartDate, selectedEndDate }) => ({
      selectedStartDate,
      selectedEndDate,
    }),
    { debounce: true },
  );

  readonly paymentInfo$ = this.select(
    ({ studentDetails, depositAmounts, selectedCurrency, flywireCheckoutSessionData, currentStep }) => ({
      studentDetails,
      depositAmounts,
      flywireCheckoutSessionData,
      currency: selectedCurrency,
      currentStep,
    }),
    { debounce: true },
  );

  readonly yourCourseData$ = this.select(
    ({ selectedSchoolLocation, selectedTimezone, selectedClassTime }) => ({
      selectedSchoolLocation,
      selectedTimezone,
      selectedClassTime,
    }),
    { debounce: true },
  );

  readonly courseDetailsWithoutCourseAndResidenceCountryAndFlow$ = this.select(
    ({ selectedLanguage, selectedSchoolLocation, selectedDuration, selectedStartDate, selectedEndDate, selectedResidence, activeFlow, selectedCourse }) => ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      selectedResidence,
      activeFlow,
      selectedProductPackage: selectedCourse?.productPackage,
    }),
    {
      debounce: true,
      equal: (previous, current) =>
        this.compareCourseDetails(previous, current, false) &&
        previous.selectedResidence?.countryCode === current.selectedResidence?.countryCode &&
        previous.activeFlow === current.activeFlow,
    },
  );
  readonly juniorCourseUpgrade$ = this.select(({ juniorCourseUpgrade }) => juniorCourseUpgrade);

  readonly courseDetails$ = this.select(
    ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedAyaDuration,
      selectedStartDate,
      selectedEndDate,
      selectedTimezone,
      selectedClassTime,
      activeFlow,
    }) => ({
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedAyaDuration,
      selectedStartDate,
      selectedEndDate,
      selectedTimezone,
      selectedClassTime,
      activeFlow,
    }),
    {
      debounce: true,
      equal: (previous, current) => {
        return this.compareCourseDetails(previous, current);
      },
    },
  );

  /**
   * Separate selector for deeplink validation that doesn't trigger circular dependencies
   * This selector is designed to emit immediately when any validation-relevant field changes
   */
  readonly validationData$ = this.select(({ activeFlow, selectedLanguage, selectedSchoolLocation, selectedCourse, selectedDuration, selectedAyaDuration }) => ({
    activeFlow,
    selectedLanguage,
    selectedSchoolLocation,
    selectedCourse,
    selectedDuration,
    selectedAyaDuration,
  }));

  readonly tripDates$ = this.select(({ tripDates }) => tripDates);

  // Selector for getting the selected residence data (country and region)
  readonly residenceData$ = this.select(({ selectedResidence }) => selectedResidence, {
    debounce: true,
    equal: (previous, current) => this.compareResidenceData(previous, current),
  });

  readonly phoneCode$ = this.select(({ selectedResidence }) => selectedResidence?.phoneCode || '');

  readonly combinedStudentResidence$ = this.select(this.studentDetails$, this.residenceData$, (studentDetails, residenceData) => ({
    studentDetails,
    residenceData,
  }));

  readonly quoteId$ = this.select(({ quoteId }) => quoteId);

  readonly selectionForQuotePrices$ = this.select(
    ({
      activeFlow,
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      selectedAccommodation,
      selectedInsuranceCode,
      selectedTransferRows,
      selectedProfessionalImmersionExperienceCode,
      selectedResidence,
      selectedPremiumAccommodationCode,
      selectedPremiumActivities,
      selectedPrivateLesson,
      selectedUnaccompaniedMinorRows,
      selectedClassTime,
      selectedTimezone,
    }) => ({
      activeFlow,
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      selectedAccommodation,
      selectedInsuranceCode,
      selectedTransferRows,
      selectedProfessionalImmersionExperienceCode,
      selectedResidence,
      selectedPremiumAccommodationCode,
      selectedPremiumActivities,
      selectedPrivateLesson,
      selectedUnaccompaniedMinorRows,
      selectedClassTime,
      selectedTimezone,
      selectedProductPackage: selectedCourse?.productPackage,
    }),
    {
      debounce: true,
      equal: (prev, curr) => {
        return (
          prev?.activeFlow === curr?.activeFlow &&
          this.compareCourseDetails(prev, curr) &&
          prev?.selectedAccommodation?.code === curr?.selectedAccommodation?.code &&
          prev?.selectedResidence?.countryCode === curr?.selectedResidence?.countryCode &&
          prev?.selectedInsuranceCode === curr?.selectedInsuranceCode &&
          prev?.selectedTransferRows === curr?.selectedTransferRows &&
          prev?.selectedPremiumAccommodationCode === curr?.selectedPremiumAccommodationCode &&
          prev?.selectedProfessionalImmersionExperienceCode === curr?.selectedProfessionalImmersionExperienceCode &&
          prev?.selectedPremiumActivities === curr?.selectedPremiumActivities &&
          prev?.selectedPrivateLesson === curr?.selectedPrivateLesson &&
          prev?.selectedUnaccompaniedMinorRows === curr?.selectedUnaccompaniedMinorRows
        );
      },
    },
  );

  readonly selectionForLeads$ = this.select(
    this.activeFlow$,
    this.selectionForQuotePrices$,
    this.studentDetails$,
    this.selectedCurrency$,
    this.quoteId$,
    (activeFlow: Flow, selectionForQuotePrices: Partial<QuoteToolLiteState>, studentDetails: StudentDetails, selectedCurrency: string, quoteId: string) => {
      return {
        activeFlow,
        ...selectionForQuotePrices,
        selectedCurrency,
        studentDetails,
        quoteId,
      };
    },
    {
      debounce: true,
    },
  );

  readonly selectionForAyaLeads$ = this.select(
    ({ activeFlow, selectedSchoolLocation, selectedCourse, selectedAyaDuration, selectedStartDate, selectedAccommodation, studentDetails }) => ({
      activeFlow,
      selectedSchoolLocation,
      selectedCourse,
      selectedAyaDuration,
      selectedStartDate,
      selectedAccommodation,
      studentDetails,
    }),
    { debounce: true },
  );

  readonly baseSelectionForDataLayer$ = this.select(
    this.activeFlow$,
    this.requestedQuotes$,
    this.selectedCourse$,
    this.selectedDuration$,
    this.tripDates$,
    (activeFlow: Flow, requestedQuotes: number, course: CourseDetails, duration: number, tripDates: TripDates) => {
      return {
        quote: requestedQuotes,
        programType: activeFlow,
        course: course?.name,
        duration,
        startDate: tripDates?.tuitionStartDate,
        endDate: tripDates?.tuitionEndDate,
      };
    },
    {
      debounce: true,
    },
  );

  readonly selectionForAdultFlowDataLayer$ = this.select(
    this.courseDetails$,
    this.selectedAccommodation$,
    (courseDetails: Partial<QuoteToolLiteState>, selectedAccommodation: BaseAccommodation) => {
      return {
        ...courseDetails,
        selectedAccommodation,
      };
    },
    {
      debounce: true,
    },
  );

  readonly selectionForAyaFlowDataLayer$ = this.select(
    ({ selectedSchoolLocation, selectedAyaDuration, selectedAccommodation }) => ({
      selectedSchoolLocation,
      selectedAyaDuration,
      selectedAccommodation,
    }),
    { debounce: true },
  );

  readonly selectionForJuniorFlowDataLayer$ = this.select(
    this.currentStepId$,
    this.courseDetails$,
    this.upselling$,
    this.selectedStartDate$,
    this.selectedEndDate$,
    (currentStepId: RouteIds, courseDetails: Partial<QuoteToolLiteState>, upselling: boolean, selectedStartDate: string, selectedEndDate: string) => {
      return {
        currentStepId,
        ...courseDetails,
        upselling,
        selectedStartDate,
        selectedEndDate,
      };
    },
    {
      debounce: true,
    },
  );

  readonly selectionForOnlineFlowDataLayer$ = this.select(
    ({ selectedTimezone, selectedClassTime }) => {
      return {
        selectedTimezoneName: selectedTimezone?.name,
        selectedClassTimeLabel: selectedClassTime?.label,
      };
    },
    { debounce: true },
  );

  readonly selectionForExtraSummary$ = this.select(
    ({ activeFlow, selectedSchoolLocation, selectedStartDate, selectedCourse }) => {
      return {
        activeFlow,
        selectedSchoolLocation,
        selectedStartDate,
        selectedProductPackage: selectedCourse?.productPackage,
      };
    },
    {
      debounce: true,
      equal: (prev, curr) =>
        prev?.activeFlow === curr?.activeFlow &&
        prev?.selectedSchoolLocation?.schoolCode === curr?.selectedSchoolLocation?.schoolCode &&
        prev?.selectedStartDate === curr?.selectedStartDate &&
        prev?.selectedProductPackage === curr?.selectedProductPackage,
    },
  );

  readonly selectedLanguageAndCourse$ = this.select(
    this.selectedLanguage$,
    this.selectedCourse$,
    (selectedLanguage, selectedCourse) => ({
      selectedLanguage,
      selectedCourse,
    }),
    {
      debounce: true,
    },
  );

  // Updaters
  readonly updateState = this.updater((state, partialState: Partial<QuoteToolLiteState>) => ({
    ...state,
    ...partialState,
  }));

  readonly updateCurrentStep = this.updater((state, currentStep: Step) => ({
    ...state,
    currentStep,
  }));

  readonly updateValidityOfCurrentStep = this.updater((state, valid: boolean) => ({
    ...state,
    currentStep: {
      ...state.currentStep,
      valid,
    },
  }));

  readonly updateAccommodationCode = this.updater((state, accommodationCode: string) => ({
    ...state,
    selectedAccommodation: {
      code: accommodationCode,
      type: state?.selectedAccommodation?.type,
    },
  }));

  readonly incrementRequestedQuotes = this.updater((state) => ({
    ...state,
    requestedQuotes: state.requestedQuotes + 1,
  }));

  /**
   * Clean all fields related to the quote itself. Ie: the school, course, accommodation, insurance, etc.
   * It keeps the student details, selected currency and selected residence.
   * For the current step, the active flow and the selected language it takes the one from the default state.
   */
  readonly resetQuote = this.updater((state) => {
    return {
      ...this.getDefaultState(state.requestedQuotes),
      studentDetails: { ...state.studentDetails },
      selectedCurrency: state.selectedCurrency,
      selectedResidence: state.selectedResidence,
    };
  });

  readonly resetSelectedExtras = this.updater((state) => {
    const otherAccommodationWithPackage = state.selectedAccommodation?.type === OTHER && state.selectedCourse?.productPackage;

    return {
      ...state,
      selectedInsuranceCode: undefined,
      selectedTransferRows: undefined,
      selectedPremiumAccommodationCode: undefined,
      selectedPremiumActivities: undefined,
      selectedProfessionalImmersionExperienceCode: undefined,
      selectedUnaccompaniedMinorRows: undefined,
      selectedPrivateLesson: undefined,

      selectedAccommodation: {
        type: otherAccommodationWithPackage ? HOMESTAY : state.selectedAccommodation?.type,
      },
    };
  });

  readonly resetStore = this.updater((state) => ({
    ...this.getDefaultState(state.requestedQuotes + 1),
  }));

  private readonly company = getCompany();
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storageKey = getQuoteToolStorageKey();

  constructor() {
    super({});
  }

  /**
   * Initializes the store with the state from the session storage if it exists.
   * If not, it will initialize the store with the default state.
   *
   * Before restoring state, checks for a cleanup flag (STORE_RESET_KEY).
   * The flag is set in two scenarios:
   * 1. User explicitly navigated away from the app (e.g., clicked "Back to Portal")
   * 2. Company changed (e.g., from Alpadia to Kaplan via URL parameter)
   *
   * When the flag is set, the store is reset to a fresh state to ensure data consistency.
   * This allows users to refresh pages without losing data, while ensuring a clean start
   * when they return after explicitly leaving or when switching companies.
   *
   * It will also add the subscription to the active flow to clean the selected fields and
   * the same for the selected school location and extras.
   */
  initializeStore() {
    // Check if reset is required (explicit navigation away or company change)
    const resetRequired = this.sessionStorageService.get(STORE_RESET_KEY);

    if (resetRequired) {
      // Reset to fresh state - clear the flag first
      this.sessionStorageService.delete(STORE_RESET_KEY);
      this.updateState({ ...this.getDefaultState() });
    } else {
      // Normal initialization - restore state if available
      const stateFromSessionStorage = <
        QuoteToolLiteState & {
          currentStepId: RouteIds;
        }
      >this.sessionStorageService.get(this.storageKey);

      if (stateFromSessionStorage && Object.keys(stateFromSessionStorage)?.length) {
        this.updateState({
          currentStep: this.stepDefinitionService.getStepDefinition(stateFromSessionStorage.currentStepId, stateFromSessionStorage.activeFlow),
          ...stateFromSessionStorage,
        });
      } else {
        // If there is no state in session storage, set the active flow based on the company
        this.updateState({ ...this.getDefaultState() });
      }
    }

    // Everytime the selected school location changes, clean the selected extras to avoid inconsistencies
    this.resetSelectedExtrasOnSchoolChange();

    // Everytime the selected product package changes, clean the selected extras to avoid inconsistencies
    this.resetSelectedExtrasOnProductPackageChange();

    // Everytime the selected duration changes, clean the selected premium activities
    this.resetPremiumActivitiesOnSelectedDurationChange();

    // Everytime the active flow changes, clean selected fields to avoid inconsistencies and preselect language depending on company
    this.activeFlow$
      .pipe(
        skip(1),
        switchMap((activeFlow: Flow) => combineLatest([of(activeFlow), this.sharedDataStore.getDefaultLanguage()])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([activeFlow, defaultLanguage]: [Flow, string]) => {
        this.setState((state) => ({
          requestedQuotes: state.requestedQuotes,
          activeFlow,
          currentStep: this.getDefaultStep(activeFlow),
          selectedLanguage: defaultLanguage,
          studentDetails: { ...state.studentDetails },
          selectedCurrency: state.selectedCurrency,
          selectedResidence: state.selectedResidence,
        }));
      });
  }

  private getDefaultState(requestedQuotes = 1) {
    return {
      currentStep: this.getDefaultStep(),
      activeFlow: this.getDefaultFlow(),
      selectedLanguage: 'en',
      requestedQuotes,
    };
  }

  /**
   * It gets the default flow based on the company. For KIL, it will be adult and for ALP junior
   */
  private getDefaultFlow() {
    return this.company === COMPANIES.ALPADIA ? Flows.Junior : Flows.Adult;
  }

  /**
   * It gets the default step. This is the course step.
   */
  private getDefaultStep(activeFlow: Flow = this.getDefaultFlow()) {
    return this.stepDefinitionService.getStepDefinition(RouteIds.course, activeFlow);
  }

  /**
   * It compares two course details by checking the equality of:
   * - selectedLanguage
   * - selectedSchoolLocation (by schoolCode)
   * - selectedCourse (by courseCode)
   * - selectedDuration
   * - selectedStartDate
   * - selectedEndDate
   * - selectedTimezone (by code)
   *
   * If withCourse flag is false, then, the selectedCourse is not compared.
   */
  private compareCourseDetails(prevCourseDetails: SelectedCourseDetails, currCourseDetails: SelectedCourseDetails, withCourse = true): boolean {
    const {
      selectedLanguage: prevLanguage,
      selectedSchoolLocation: { schoolCode: prevSchoolCode } = {},
      selectedDuration: prevDuration,
      selectedStartDate: prevStartDate,
      selectedEndDate: prevEndDate,
      selectedAyaDuration: prevAyaDuration,
      selectedTimezone: prevTimezone,
      selectedClassTime: prevClassTime,
    } = prevCourseDetails;

    const {
      selectedLanguage: currLanguage,
      selectedSchoolLocation: { schoolCode: currSchoolCode } = {},
      selectedDuration: currDuration,
      selectedStartDate: currStartDate,
      selectedAyaDuration: currAyaDuration,
      selectedEndDate: currEndDate,
      selectedTimezone: currTimezone,
      selectedClassTime: currClassTime,
    } = currCourseDetails;

    const sameObjects =
      prevLanguage === currLanguage &&
      prevSchoolCode === currSchoolCode &&
      prevDuration === currDuration &&
      prevStartDate === currStartDate &&
      prevEndDate === currEndDate &&
      prevAyaDuration?.weeks === currAyaDuration?.weeks &&
      prevAyaDuration?.months === currAyaDuration?.months &&
      prevTimezone?.code === currTimezone?.code &&
      prevClassTime?.startTime === currClassTime?.startTime &&
      prevClassTime?.endTime === currClassTime?.endTime;

    if (!withCourse) {
      return sameObjects;
    }

    const { selectedCourse: { code: prevCourseCode } = {} } = prevCourseDetails;
    const { selectedCourse: { code: currCourseCode } = {} } = currCourseDetails;
    return sameObjects && prevCourseCode === currCourseCode;
  }

  private compareResidenceData(previous: ResidenceDetails, current: ResidenceDetails): boolean {
    if (!previous || !current) {
      return false;
    }

    const { countryCode: prevCountry, regionCode: prevRegion, phoneCode: prevPhoneCode, currency: prevCurrency } = previous;
    const { countryCode: currCountry, regionCode: currRegion, phoneCode: currPhoneCode, currency: currCurrency } = current;

    return prevCountry === currCountry && prevRegion === currRegion && prevPhoneCode === currPhoneCode && prevCurrency === currCurrency;
  }

  private resetSelectedExtrasOnSchoolChange() {
    this.selectedSchoolLocation$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetSelectedExtras();
    });
  }

  /**
   * Resets the selected extras when the product package changes.
   */
  private resetSelectedExtrasOnProductPackageChange() {
    this.productPackage$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetSelectedExtras();
    });
  }

  private resetPremiumActivitiesOnSelectedDurationChange() {
    this.selectedDuration$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateState({ selectedPremiumActivities: undefined });
    });
  }
}
